import { useState } from "react";
import { Plus, Trash2, CalendarDays } from "lucide-react";
import { PROVINCE_COLORS } from "@/lib/constants";

const inputCls = "w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 outline-none focus:border-[#C8102E] focus:ring-2 focus:ring-[#C8102E]/20";
const today = () => new Date().toISOString().slice(0, 10);

const ROUND_FIELDS = [
  { key: "date", type: "date", testId: "date" },
  { key: "cutoff", type: "number", testId: "cutoff", placeholder: "—" },
  { key: "invitations", type: "number", testId: "invites", placeholder: "—" },
  { key: "note", type: "text", testId: "note", placeholder: "e.g. General draw" },
];

function RoundRow({ r, i, onChange, onRemove }) {
  return (
    <tr data-testid={`admin-draw-row-${i}`}>
      {ROUND_FIELDS.map((f) => (
        <td key={f.key} className="py-2 pr-3">
          <input type={f.type} min={f.type === "number" ? 0 : undefined} value={r[f.key] ?? ""} placeholder={f.placeholder}
            data-testid={`admin-draw-${f.testId}-${i}`} onChange={(e) => onChange({ [f.key]: e.target.value })} className={inputCls} />
        </td>
      ))}
      <td className="py-2"><button onClick={onRemove} data-testid={`admin-draw-delete-${i}`} aria-label="Delete round" className="rounded-md p-1.5 text-slate-400 hover:bg-[#FEF2F2] hover:text-[#C8102E]"><Trash2 className="h-4 w-4" /></button></td>
    </tr>
  );
}

export function DrawsEditor({ streams, draws, original, onChange }) {
  const [sel, setSel] = useState(streams[0]?.id || "");
  const stream = streams.find((s) => s.id === sel);
  const rounds = draws[sel] || [];
  const dirty = JSON.stringify(rounds) !== JSON.stringify(original[sel] || []);

  const setRounds = (next) => onChange({ ...draws, [sel]: next });
  const update = (i, patch) => setRounds(rounds.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i) => setRounds(rounds.filter((_, j) => j !== i));
  const add = () => setRounds([{ _key: `new-${Date.now()}`, date: today(), cutoff: stream?.cutoff ?? "", invitations: "", note: "" }, ...rounds]);
  const rowKey = (r, i) => r._key || `${r.date}|${r.cutoff}|${r.invitations}|${r.note}|${i}`;
  const unit = stream?.scoring_model === "crs_based" ? "CRS" : stream?.scoring_model === "grid" ? "points" : "score";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-draws-editor">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <label className="block flex-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Stream</span>
          <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="admin-draws-stream-select" className={`${inputCls} mt-1`}>
            {streams.map((s) => <option key={s.id} value={s.id}>{s.province} · {s.name}{(draws[s.id] || []).length ? ` (${draws[s.id].length})` : ""}</option>)}
          </select>
        </label>
        <button onClick={add} data-testid="admin-draws-add" className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#0F172A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1E293B]">
          <Plus className="h-4 w-4" /> Add round
        </button>
      </div>
      {stream && (
        <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
          <span className="rounded px-1.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: PROVINCE_COLORS[stream.province] }}>{stream.province}</span>
          {stream.stream_group} · cutoff recorded in {unit}{dirty && <span className="font-semibold text-amber-600">· unsaved changes</span>}
        </p>
      )}
      {rounds.length === 0 ? (
        <p className="mt-6 flex items-center gap-2 rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-500" data-testid="admin-draws-empty"><CalendarDays className="h-4 w-4" /> No rounds recorded for this stream yet. Add the latest invitation round above.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
              <tr><th className="pb-2 pr-3 w-40">Date</th><th className="pb-2 pr-3 w-28">Cutoff</th><th className="pb-2 pr-3 w-28">Invitations</th><th className="pb-2 pr-3">Round / note</th><th className="pb-2 w-10" /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rounds.map((r, i) => <RoundRow key={rowKey(r, i)} r={r} i={i} onChange={(patch) => update(i, patch)} onRemove={() => remove(i)} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
